import PropTypes from "prop-types";
import React from "react";

import { Text, textPropTypes } from "@swa-ui/core";
import { swaDate } from "@swa-ui/date";
import i18n from "@swa-ui/locale";

/**
 * FlightTimes displays formatted time information.
 */
export const FlightTime = (props) => {
  const {
    className,
    indicator,
    inputFormat = "HH:mm",
    outputFormat = "h:mmA",
    textProps = {},
    textPropsMeridiem,
    time,
    type,
  } = props;

  return (
    <div {...getProps()}>
      {isMeridiem() ? renderTimeAndMeridiem() : renderTime()}
      {indicator}
    </div>
  );

  function renderTimeAndMeridiem() {
    return (
      <>
        {renderTimeWithoutMeridiem()}
        {renderMeridiem()}
      </>
    );
  }

  function renderTimeWithoutMeridiem() {
    return <Text {...getTimeProps()}>{getTimeContent().slice(0, -2)}</Text>;
  }

  function renderMeridiem() {
    return <Text {...getMeridiemProps()}>{getTimeContent().slice(-2)}</Text>;
  }

  function renderTime() {
    return <Text {...getTimeProps()}>{getTimeContent()}</Text>;
  }

  function getProps() {
    return {
      ["aria-label"]: getAriaLabel(),
      className: className,
      role: "text",
    };
  }

  function getTimeProps() {
    return {
      element: "span",
      ...textProps,
    };
  }

  function getMeridiemProps() {
    return {
      element: "span",
      ...textPropsMeridiem,
    };
  }

  function getAriaLabel() {
    let typeLabel = "";

    if (type === "destination") {
      typeLabel = i18n("FlightTime__ARRIVES_ARIA_LABEL");
    } else if (type === "origination") {
      typeLabel = i18n("FlightTime__DEPARTS_ARIA_LABEL");
    }

    return `${typeLabel} ${isMeridiem() ? getAmPmAriaLabel() : getTimeContent()}`;
  }

  function getTimeContent() {
    return swaDate(time, inputFormat).format(outputFormat);
  }

  function getAmPmAriaLabel() {
    return getTimeContent().toUpperCase().replace("AM", " A M").replace("PM", " P M");
  }

  function isMeridiem() {
    return outputFormat.indexOf("A") !== -1 || outputFormat.indexOf("a") !== -1;
  }
};

export const flightTimePropTypes = {
  /** Additional class to be added to the outermost element for positioning the component. */
  className: PropTypes.string,

  /** Indicator that is rendered below the time. */
  indicator: PropTypes.node,

  /**
   * Format of the time prop string. Typically this would be something like: "hh:mm", but can be
   * anything that swaDate/DayJS supports.
   */
  inputFormat: PropTypes.string,

  /**
   * Format the time that is rendered. Typically this would be something like: "h:mmA", but can be
   * anything that swaDate/DayJS supports.
   */
  outputFormat: PropTypes.string,

  /** Props to pass to Text component that will style the time portion of what is rendered. */
  textProps: PropTypes.shape(textPropTypes),

  /**
   * Props to pass to Text component that will style the "AM/PM" portion of what is rendered. This
   * prop is only applicable when the outputFormat includes "a" or "A".
   */
  textPropsMeridiem: PropTypes.shape(textPropTypes),

  /** Time value to be passed to the component. */
  time: PropTypes.string.isRequired,

  /** Defines which string to use for aria-label. */
  type: PropTypes.oneOf(["destination", "origination"]),
};

FlightTime.propTypes = flightTimePropTypes;
